import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { History, ChevronDown, ChevronRight, PhoneOff } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "");

/** Small pill on an active number that came from a client refresh. */
export function RefreshedPill({ number }) {
  if (number?.source !== "client_refresh") return null;
  return (
    <Badge variant="outline" className="text-[9px] h-4 px-1 text-indigo-700 border-indigo-200 bg-indigo-50"
           title={`Client refresh · ${fmt(number.refreshed_at)}`} data-testid={`refreshed-pill-${number.e164}`}>
      Refreshed
    </Badge>
  );
}

/** Retired numbers. Default: collapsed "Replaced numbers" block. `box` mode: always-open
 *  "Customer Number" box used by the SOHO two-box layout (old above, new below). */
export default function RetiredNumbers({ numbers = [], box = false }) {
  const [open, setOpen] = useState(box);
  if (!numbers.length) return null;
  return (
    <div className={`border border-slate-200 rounded bg-slate-50/60 ${box ? "" : "mt-2"}`} data-testid="retired-numbers">
      {box ? (
        <div className="flex items-center gap-1.5 px-2 py-1.5 text-[10px] uppercase tracking-wider text-slate-500"
             data-testid="customer-number-box-title">
          <History className="w-3 h-3 text-slate-400" />
          Customer Number <span className="font-mono normal-case text-slate-400">({numbers.length} replaced by client)</span>
        </div>
      ) : (
        <button type="button" onClick={() => setOpen((o) => !o)}
                className="w-full flex items-center gap-1.5 px-2 py-1.5 text-[11px] text-slate-600 hover:bg-slate-100 rounded"
                data-testid="retired-numbers-toggle">
          {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          <History className="w-3 h-3 text-slate-400" />
          Replaced numbers <span className="font-mono text-slate-400">({numbers.length})</span>
        </button>
      )}
      {open && (
        <div className="px-2 pb-2 flex flex-col gap-1">
          {numbers.map((n, i) => (
            <div key={`${n.e164}-${n.retired_at}-${i}`} className="flex items-center justify-between px-2 py-1 rounded border border-slate-200 bg-white"
                 data-testid={`retired-num-${n.e164}`}>
              <div className="flex items-center gap-2 min-w-0">
                <PhoneOff className="w-3 h-3 text-slate-300 shrink-0" />
                <span className="font-mono text-[11px] line-through text-slate-400">{n.e164}</span>
                {n.wrong_number && (
                  <Badge variant="outline" className="text-[9px] h-4 px-1 text-rose-600 border-rose-200 bg-rose-50">wrong</Badge>
                )}
              </div>
              <span className="text-[10px] text-slate-400 whitespace-nowrap">Replaced by client · {fmt(n.retired_at)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
